import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * ID based MultiSelect
 *
 * selectedValues MUST be an array of IDs.
 * Example: [130, 311, 70]
 *
 * No category_list / subcategory / speciality_name fallback is used here.
 */
const MultiSelect = ({
  options = [],
  selectedValues = [],
  onChange,
  name,
  placeholder = "Select...",
  idField,
  idAsArray = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  // Normalize all option values to strings so 130 and "130" always match.
  const normalizedOptions = useMemo(() => {
    const seen = new Set();

    return (options || []).reduce((result, option) => {
      if (option?.value === undefined || option?.value === null) return result;

      const value = String(option.value);
      if (seen.has(value)) return result;

      seen.add(value);
      result.push({
        value,
        label: option.label ?? value,
      });

      return result;
    }, []);
  }, [options]);

  // Formik values are the single source of truth.
  const selectedIds = useMemo(() => {
    if (!Array.isArray(selectedValues)) return [];

    return [...new Set(
      selectedValues
        .map(Number)
        .filter(Number.isFinite)
        .map(String)
    )];
  }, [selectedValues]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const valueToLabel = useMemo(() => {
    return new Map(
      normalizedOptions.map(option => [String(option.value), option.label])
    );
  }, [normalizedOptions]);

  const filteredOptions = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();
    if (!search) return normalizedOptions;

    return normalizedOptions.filter(option =>
      String(option.label).toLowerCase().includes(search)
    );
  }, [normalizedOptions, searchTerm]);

  const updateSelection = (nextIds) => {
    const ids = [...new Set(
      nextIds
        .map(Number)
        .filter(Number.isFinite)
    )];

    // Main field is also ID based.
    onChange(name, ids);

    // Explicit ID field used by the API payload.
    if (idField) {
      onChange(idField, idAsArray ? ids : ids.join(","));
    }
  };

  const toggleOption = (value) => {
    const id = Number(value);
    if (!Number.isFinite(id)) return;

    const next = selectedSet.has(String(id))
      ? selectedIds.filter(item => item !== String(id))
      : [...selectedIds, String(id)];

    updateSelection(next);
  };

  const removeOption = (id) => {
    updateSelection(selectedIds.filter(item => item !== String(id)));
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <div
        className="flex flex-wrap gap-2 items-center min-h-[42px] w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm cursor-pointer"
        onClick={() => setIsOpen(true)}
      >
        {selectedIds.length === 0 ? (
          <span className="text-gray-400">{placeholder}</span>
        ) : (
          selectedIds.map(id => (
            <div
              key={id}
              className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full flex items-center"
            >
              {valueToLabel.get(id) ?? `ID: ${id}`}
              <button
                type="button"
                className="ml-2 text-blue-500 hover:text-blue-700"
                onClick={(event) => {
                  event.stopPropagation();
                  removeOption(id);
                }}
              >
                &times;
              </button>
            </div>
          ))
        )}
      </div>

      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
          <div className="sticky top-0 bg-white p-2 border-b">
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
          </div>

          {filteredOptions.length > 0 ? (
            filteredOptions.map(option => {
              const id = String(option.value);
              const checked = selectedSet.has(id);

              return (
                <label
                  key={id}
                  className={`flex items-center px-4 py-2 hover:bg-gray-100 cursor-pointer ${
                    checked ? "bg-blue-50" : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    readOnly
                    className="mr-2 h-4 w-4"
                    onChange={() => toggleOption(id)}
                  />
                  <span>{option.label}</span>
                </label>
              );
            })
          ) : (
            <div className="px-4 py-2 text-gray-500">No options found</div>
          )}
        </div>
      )}
    </div>
  );
};

export default MultiSelect;
